import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Toolbar,
  Box,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  TableChart as TableChartIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useWebSocket } from '../WebSocketContext';

// Theme colors
const primaryColor = '#5757b0';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '95vw',
    maxHeight: '90vh', // Reduced from 95vh to ensure it fits on screen
    width: '85vw',
    height: '80vh', // Reduced from 85vh to be more conservative
    backgroundColor: '#f5f5f5',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 8px 32px rgba(87, 87, 176, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  '& .MuiDialog-paper.MuiDialog-paperFullScreen': {
    width: '100vw',
    height: '100vh',
    maxWidth: '100vw',
    maxHeight: '100vh',
    borderRadius: 0,
    margin: 0,
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiDialog-paper': {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      borderRadius: 0,
      margin: 0,
    },
  },
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(87, 87, 176, 0.15)',
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: `linear-gradient(135deg, ${primaryColor} 0%, #6b6bce 50%, #8080d7 100%)`,
  color: 'white',
  padding: '12px 20px',
  paddingTop: 'calc(12px + env(safe-area-inset-top))',
  paddingLeft: 'calc(20px + env(safe-area-inset-left))',
  paddingRight: 'calc(20px + env(safe-area-inset-right))',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontWeight: 700,
  fontSize: '1.1rem',
  position: 'relative',
  boxShadow: '0 2px 8px rgba(87, 87, 176, 0.25)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
  '& .MuiTypography-root': {
    flex: 1,
    margin: 0,
    fontWeight: 600,
    fontSize: '1.1rem',
    letterSpacing: '0.025em',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '10px 12px',
    paddingTop: 'calc(10px + env(safe-area-inset-top))',
    paddingLeft: 'calc(12px + env(safe-area-inset-left))',
    paddingRight: 'calc(12px + env(safe-area-inset-right))',
    fontSize: '1rem',
    flexWrap: 'wrap',
    gap: '8px',
    '& .MuiTypography-root': {
      fontSize: '1rem',
      minWidth: 0,
      wordBreak: 'break-word',
    },
  },
}));

const ToolbarContainer = styled(Toolbar)(({ theme }) => ({
  backgroundColor: 'white',
  borderBottom: '1px solid rgba(87, 87, 176, 0.1)',
  minHeight: '48px !important',
  padding: '0 20px',
  justifyContent: 'space-between',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  [theme.breakpoints.down('sm')]: {
    padding: '0 12px',
    minHeight: '44px !important',
    flexWrap: 'wrap',
    gap: '8px',
    justifyContent: 'flex-start',
  },
}));

const ExcelContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#f8f9fa',
  overflow: 'visible',
  maxHeight: '85vh', // Limit container height to 85% of viewport
  padding: '4px',
  boxSizing: 'border-box',
  [theme.breakpoints.down('sm')]: {
    maxHeight: '100%',
    padding: '0',
  },
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: '1px solid #e0e0e0',
  backgroundColor: 'white',
  '& .MuiTab-root': {
    textTransform: 'none',
    minWidth: 120,
    fontWeight: 500,
    color: '#666',
    '&.Mui-selected': {
      color: primaryColor,
    },
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiTab-root': {
      minWidth: 90,
      fontSize: '0.85rem',
    },
  },
  '& .MuiTabs-indicator': {
    backgroundColor: primaryColor,
  },
}));

const ExcelViewer = ({ open, onClose, filename = '', eventId = null }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [excelData, setExcelData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeSheet, setActiveSheet] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);
  const tableContainerRef = useRef(null);
  const { subscribeToMessages } = useWebSocket();

  // Add scrollbar styling when component mounts
  useEffect(() => {
    if (tableContainerRef.current) {
      // Create style element for webkit scrollbars
      const style = document.createElement('style');
      style.textContent = `
        .excel-scrollable-table::-webkit-scrollbar {
          width: 14px !important;
          height: 14px !important;
        }
        .excel-scrollable-table::-webkit-scrollbar-track {
          background: #f1f1f1 !important;
          border-radius: 7px !important;
        }
        .excel-scrollable-table::-webkit-scrollbar-thumb {
          background: #888 !important;
          border-radius: 7px !important;
          border: 2px solid #f1f1f1 !important;
        }
        .excel-scrollable-table::-webkit-scrollbar-thumb:hover {
          background: #666 !important;
        }
        .excel-scrollable-table::-webkit-scrollbar-corner {
          background: #f1f1f1 !important;
        }
      `;
      
      document.head.appendChild(style);
      
      return () => {
        document.head.removeChild(style);
      };
    }
  }, [open]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setExcelData(null);
      setLoading(false);
      setError(null);
      setActiveSheet(0);
      setSearchTerm('');
    } else {
      setLoading(true);
      setError(null);
    }
  }, [open, eventId]);

  const handleExcelStreamMessage = useCallback((message) => {
    switch (message.status) {
      case 'excel_stream_start':
        if (eventId && message.event_id === eventId) {
          setLoading(true);
          setError(null);
        }
        break;

      case 'excel_data':
        if (eventId && message.event_id === eventId) {
          try {
            const data = typeof message.data === 'string' ? JSON.parse(message.data) : message.data;
            setExcelData(data);
            setLoading(false);
          } catch (error) {
            console.error('Error parsing Excel data:', error);
            setError('Failed to parse Excel data');
            setLoading(false);
          }
        }
        break;

      case 'error':
        if (eventId && message.event_id === eventId) {
          setError(message.message || 'Failed to load Excel file');
          setLoading(false);
        }
        break;

      default:
        break;
    }
  }, [eventId]);

  useEffect(() => {
    if (!open || !eventId) return;

    const unsubscribe = subscribeToMessages(['openfile'], (parsedMessage) => {
      if (parsedMessage.data &&
          ['excel_stream_start', 'excel_data', 'error'].includes(parsedMessage.data.status) &&
          parsedMessage.data.event_id === eventId) {
        handleExcelStreamMessage(parsedMessage.data);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [open, eventId, subscribeToMessages, handleExcelStreamMessage]);

  // Timeout handler
  useEffect(() => {
    if (!open || !loading || excelData || error) return;

    const timeout = setTimeout(() => {
      setError('Timeout waiting for Excel content. The file might be corrupted or there might be a server issue.');
      setLoading(false);
    }, 15000);

    return () => {
      clearTimeout(timeout);
    };
  }, [open, loading, excelData, error]);

  const downloadExcel = () => {
    if (excelData) {
      // Create CSV from the current sheet
      const currentSheet = excelData.sheets[activeSheet];
      if (currentSheet && currentSheet.data) {
        const csv = currentSheet.data.map(row => 
          row.map(cell => `"${cell}"`).join(',')
        ).join('\n');
        
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${filename}_${currentSheet.name || 'sheet'}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (containerRef.current && containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handleSheetChange = (event, newValue) => {
    setActiveSheet(newValue);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const filterRows = (rows) => {
    if (!searchTerm) return rows;
    
    return rows.filter(row =>
      row.some(cell =>
        cell && cell.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  };

  return (
    <StyledDialog 
      open={open} 
      onClose={onClose}
      maxWidth={false}
      fullScreen={isFullscreen || isSmallScreen}
    >
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <TableChartIcon sx={{ 
            mr: 1.5, 
            fontSize: '1.4rem',
            filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2))'
          }} />
          <Typography variant="h6" component="div" sx={{ margin: 0 }}>
            {filename ? `${t('viewer.excel_viewer_title')} - ${filename}` : t('viewer.excel_viewer_title')}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ 
            color: 'white',
            marginLeft: 'auto',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(4px)',
            borderRadius: '8px',
            width: '36px',
            height: '36px',
            transition: 'all 0.3s ease',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              transform: 'scale(1.05)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              borderColor: 'rgba(255, 255, 255, 0.4)',
            },
          }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <ToolbarContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            onClick={downloadExcel} 
            disabled={!excelData}
            size="small"
            title={t('viewer.download_excel')}
          >
            <DownloadIcon />
          </IconButton>
          <IconButton 
            onClick={toggleFullscreen}
            size="small"
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            size="small"
            placeholder={t('viewer.search_placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={clearSearch}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ width: 200 }}
          />
        </Box>
      </ToolbarContainer>

      <DialogContent sx={{ 
        padding: '4px', // Reduced padding to maximize table space
        height: 'calc(100% - 8px)', // Account for reduced padding
        overflow: 'visible',
        boxSizing: 'border-box'
      }}>
        <ExcelContainer ref={containerRef}>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              gap: 2
            }}>
              <LinearProgress sx={{ width: 300 }} />
              <Typography color="text.secondary">
                {t('viewer.loading_excel')}
              </Typography>
            </Box>
          ) : error ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              padding: 4
            }}>
              <Typography color="error" variant="h6" gutterBottom>
                {t('viewer.error_loading_excel')}
              </Typography>
              <Typography color="text.secondary" sx={{ textAlign: 'center' }}>
                {error}
              </Typography>
            </Box>
          ) : excelData && excelData.sheets && excelData.sheets.length > 0 ? (
            <>
              {/* Sheet tabs */}
              {excelData.sheets.length > 1 && (
                <StyledTabs
                  value={activeSheet}
                  onChange={handleSheetChange}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  {excelData.sheets.map((sheet, index) => (
                    <Tab
                      key={index}
                      label={sheet.name || `Sheet ${index + 1}`}
                      value={index}
                    />
                  ))}
                </StyledTabs>
              )}

              {/* Table content - Using div instead of TableContainer */}
              <div 
                ref={tableContainerRef}
                className="excel-scrollable-table"
                style={{
                  flex: 1,
                  overflow: 'auto',
                  overflowX: 'scroll',
                  overflowY: 'scroll',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  margin: '8px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  height: isSmallScreen ? 'calc(100vh - 260px)' : 'calc(85vh - 200px)',
                  width: 'calc(100% - 16px)',
                  maxHeight: isSmallScreen ? 'calc(100vh - 260px)' : 'calc(85vh - 200px)',
                  minHeight: '400px', // Reduced minimum height
                  scrollbarWidth: 'auto',
                  scrollbarColor: '#888 #f1f1f1',
                  position: 'relative',
                  boxSizing: 'border-box',
                }}
              >
                <Table stickyHeader sx={{
                  minWidth: '1200px', // Force table to be wider than most containers
                  tableLayout: 'fixed', // Force fixed layout for consistent widths
                  '& .MuiTableCell-root': {
                    borderRight: '1px solid #e0e0e0',
                    borderBottom: '1px solid #e0e0e0',
                    padding: '12px 16px', // Increased padding for better spacing
                    fontSize: '0.875rem',
                    width: '200px', // Fixed width instead of min/max
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    cursor: 'default',
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                      backgroundColor: '#f5f5f5', // Light hover effect
                    }
                  },
                  '& .MuiTableCell-head': {
                    backgroundColor: '#f8f9fa',
                    fontWeight: 600,
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    width: '200px', // Fixed width for headers too
                    borderBottom: '2px solid #dee2e6',
                    color: '#495057',
                    fontSize: '0.9rem',
                    '&:hover': {
                      backgroundColor: '#e9ecef',
                    }
                  },
                  '& .MuiTableRow-root': {
                    '&:nth-of-type(even)': {
                      backgroundColor: '#fafafa', // Zebra striping for better readability
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd !important', // Row hover effect
                      '& .MuiTableCell-root': {
                        backgroundColor: 'transparent',
                      }
                    }
                  }
                }}>
                  <TableHead>
                    <TableRow>
                      {excelData.sheets[activeSheet]?.headers?.map((header, index) => (
                        <TableCell key={index} align="left">
                          <Tooltip 
                            title={header || `Column ${index + 1}`} 
                            placement="top"
                            arrow
                            enterDelay={500}
                          >
                            <span style={{ display: 'block' }}>
                              {header}
                            </span>
                          </Tooltip>
                        </TableCell>
                      )) || (
                        // If no headers, create column headers A, B, C, etc.
                        excelData.sheets[activeSheet]?.data?.[0]?.map((_, index) => (
                          <TableCell key={index} align="left">
                            <Tooltip 
                              title={`Column ${String.fromCharCode(65 + index)}`} 
                              placement="top"
                              arrow
                              enterDelay={500}
                            >
                              <span style={{ display: 'block' }}>
                                {String.fromCharCode(65 + index)}
                              </span>
                            </Tooltip>
                          </TableCell>
                        ))
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filterRows(excelData.sheets[activeSheet]?.data || []).map((row, rowIndex) => (
                      <TableRow key={rowIndex}>
                        {row.map((cell, cellIndex) => (
                          <TableCell key={cellIndex} align="left">
                            <Tooltip 
                              title={cell || 'Empty cell'} 
                              placement="top"
                              arrow
                              enterDelay={300}
                              sx={{
                                '& .MuiTooltip-tooltip': {
                                  maxWidth: '400px',
                                  fontSize: '0.875rem',
                                  backgroundColor: '#333',
                                  color: 'white',
                                  whiteSpace: 'pre-wrap', // Preserve line breaks
                                  wordBreak: 'break-word'
                                }
                              }}
                            >
                              <span style={{ 
                                display: 'block',
                                width: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {cell || ''}
                              </span>
                            </Tooltip>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              padding: 4
            }}>
              <Typography color="text.secondary">
                {t('viewer.no_excel_content')}
              </Typography>
            </Box>
          )}
        </ExcelContainer>
      </DialogContent>
    </StyledDialog>
  );
};

export default ExcelViewer;
